import { Modal } from '@Wonder-Cave/ui';
import { ISearchClientsRecord, ISearchTcrCampaignsRecord } from '@shared/models';
import { Formik, FormikProps } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { IClientLocalSystemNumberRecord } from '../../../../hooks/useClientLocalSystemNumbers';
import { searchSystemNumbers } from '../../../Numbers/types';
import {
  IPurchaseProviderResult,
  allocateNumbersSequentially,
  buildProviderFailureRows,
  buildRow,
  classifyAllocatedRow,
  groupCampaignsByProvider,
  resolveCampaignContext,
  uniqueSorted,
} from './number-management.helpers';
import PurchaseNumbersForm from './PurchaseNumbersForm';

// Re-exported from number-management.helpers so existing consumers (PurchaseNumbersReviewModal,
// number-management.handlers) keep their `from './PurchaseNumbersModal'` imports unchanged.
export type { IPurchaseProviderResult };

export interface IPurchaseReviewSummary {
  areaCode: string;
  quantity: number;
  results: IPurchaseProviderResult[];
}

export interface IPurchaseForm {
  selectedProviderNames: string[];
  tcrCampaignIds: string[];
  areaCode: string;
  quantity: number;
}

interface IPurchaseNumbersModalProps {
  show: boolean;
  records: IClientLocalSystemNumberRecord[];
  allTcrCampaigns: ISearchTcrCampaignsRecord[];
  tcrCampaignsLoading: boolean;
  client: ISearchClientsRecord | undefined;
  onClose: () => void;
  onReview: (summary: IPurchaseReviewSummary) => void;
  onConfirm: (summary: IPurchaseReviewSummary) => void;
  isConfirming: boolean;
  resetKey: number;
}

const buildSchema = (maxQuantity: number) =>
  yup.object().shape({
    selectedProviderNames: yup.array().of(yup.string().required()).min(1, 'At least one provider is required'),
    tcrCampaignIds: yup
      .array()
      .of(yup.string().required())
      .min(1, 'At least one TCR Campaign is required')
      .test('unique', 'Duplicate TCR campaigns selected', val => !val || val.length === new Set(val).size),
    areaCode: yup
      .string()
      .matches(/^[0-9]+$/, 'Must be only digits')
      .length(3, 'Must be exactly 3 digits')
      .required('Area Code is required'),
    quantity: yup
      .number()
      .integer('Must be a whole number')
      .required('Quantity is required')
      .min(1, 'Must be >= 1')
      .max(maxQuantity, `Must be <= ${maxQuantity}`),
  });

const PurchaseNumbersModal = ({ show, records, allTcrCampaigns, tcrCampaignsLoading, client, onClose, onReview, onConfirm, isConfirming, resetKey }: IPurchaseNumbersModalProps) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<IPurchaseReviewSummary | null>(null);
  const [searchErrors, setSearchErrors] = useState<string[]>([]);

  const formikRef = useRef<FormikProps<IPurchaseForm>>(null);

  const allProviderNames = useMemo(
    () => uniqueSorted(allTcrCampaigns.filter(c => c.isActive).map(c => c.provider?.name)),
    [allTcrCampaigns],
  );

  const initialValues = useMemo<IPurchaseForm>(() => ({
    selectedProviderNames: allProviderNames,
    tcrCampaignIds: [],
    areaCode: client?.areaCode?.toString() ?? '',
    quantity: 7,
  }), [allProviderNames, client?.areaCode]);

  // Reset form and search state when the parent increments resetKey (close or post-purchase)
  useEffect(() => {
    if (resetKey === 0) return;
    formikRef.current?.resetForm({ values: initialValues });
    setSearchResult(null);
    setSearchErrors([]);
  }, [resetKey, initialValues]);

  // Sync form fields whenever the modal is opened or derived values change.
  // Providers are backfilled when the list populates after mount.
  // Area code is backfilled when the client record arrives after mount (areaCode starts empty).
  useEffect(() => {
    if (!show) return;
    const form = formikRef.current;
    if (!form) return;
    if (form.values.selectedProviderNames.length === 0 && allProviderNames.length > 0) {
      form.setFieldValue('selectedProviderNames', allProviderNames);
    }
    const clientAreaCode = client?.areaCode?.toString() ?? '';
    if (!form.values.areaCode && clientAreaCode) {
      form.setFieldValue('areaCode', clientAreaCode);
    }
  }, [show, allProviderNames, client?.areaCode]);

  const handleSearch = async (values: IPurchaseForm) => {
    if (!client?.environment) return;

    setIsSearching(true);
    setSearchResult(null);
    setSearchErrors([]);

    // Resolve every selected campaign to its GR id, provider, and 49-cap slot count.
    const contexts = values.tcrCampaignIds.map(id => resolveCampaignContext(id, records, allTcrCampaigns));

    // Campaigns that can't be searched produce their rows directly (no GR request):
    //  - missing GR id: cannot key a search.
    //  - unresolved provider (falls back to the campaign id): would form a singleton group and
    //    silently re-create the one-search-per-campaign bug this fixes — error out instead.
    //  - at cap (availableSlots === 0): a limit: 0 search makes GR return all numbers unbounded.
    const directRows = new Map<string, IPurchaseProviderResult>();
    const searchableContexts = contexts.filter(context => {
      if (!context.grTcrCampaignId) {
        directRows.set(context.tcrCampaignId, buildRow(context, [], `No GR campaign record found for ${context.tcrCampaignId}. Please contact support.`));
        return false;
      }
      if (context.providerName === context.tcrCampaignId) {
        directRows.set(context.tcrCampaignId, buildRow(context, [], `Provider could not be determined for ${context.tcrCampaignId}. Please contact support.`));
        return false;
      }
      if (context.availableSlots === 0) {
        // At cap: skip the GR search (limit: 0 returns all numbers unbounded), but still flag
        // the capacity shortfall — the campaign can't take the requested quantity. classifyAllocatedRow
        // emits the "N/49 ... Cannot order Q" capacity message since availableSlots (0) < quantity.
        directRows.set(context.tcrCampaignId, classifyAllocatedRow(context, [], values.quantity, values.areaCode));
        return false;
      }
      return true;
    });

    const providerGroups = Array.from(groupCampaignsByProvider(searchableContexts).values());

    // One search request per provider for the SUMMED capped need across that provider's
    // campaigns. The raised backend cap accepts limit > 49; GR returns a distinct set and
    // may return fewer than requested (handled by sequential allocation below).
    const settled = await Promise.allSettled(
      providerGroups.map(async group => {
        const totalLimit = group.reduce((sum, c) => sum + Math.min(values.quantity, c.availableSlots), 0);
        const result = await searchSystemNumbers({
          environment: client.environment!,
          tcrCampaignId: group[0].grTcrCampaignId!,
          limit: totalLimit,
          areaCode: values.areaCode,
          // Exact-NPA search: GR must not substitute a nearby area code when inventory is short.
          allowAreaCodeReplacement: false,
        });
        return result.data?.numbers ?? [];
      }),
    );

    // Fan provider outcomes back out into per-campaign rows.
    const searchedRows = settled.reduce<Map<string, IPurchaseProviderResult>>((acc, outcome, idx) => {
      const group = providerGroups[idx];
      const rows = outcome.status === 'fulfilled'
        ? allocateNumbersSequentially(outcome.value, group, values.quantity)
            .map(({ tcrCampaignId, phoneNumbers }) => {
              const context = group.find(c => c.tcrCampaignId === tcrCampaignId)!;
              return classifyAllocatedRow(context, phoneNumbers, values.quantity, values.areaCode);
            })
        : buildProviderFailureRows(group, outcome.reason);
      return rows.reduce((map, row) => new Map(map).set(row.tcrCampaignId, row), acc);
    }, new Map<string, IPurchaseProviderResult>());

    // Re-emit one row per campaign in the original selection order so downstream row
    // matching (review modal, confirm, failure annotation) stays stable.
    const results: IPurchaseProviderResult[] = values.tcrCampaignIds.map(
      id => directRows.get(id) ?? searchedRows.get(id)!,
    );

    const errors = results.filter(r => r.error !== null).map(r => r.error as string);
    setSearchErrors(errors);

    const summary: IPurchaseReviewSummary = {
      areaCode: values.areaCode,
      quantity: values.quantity,
      results,
    };
    setSearchResult(summary);
    setIsSearching(false);
  };

  const titleComponent = (
    <div className='flex flex-col mt-4 mb-4'>
      <h2 className='w-full'>Purchase Numbers</h2>
    </div>
  );

  // Formik is rendered outside Modal so it stays mounted while the review modal
  // is open, preserving form state when the user clicks "Back to edit".
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={(values) => {
        try {
          buildSchema(49).validateSync(values, { abortEarly: false });
        } catch (e: unknown) {
          if (!(e instanceof yup.ValidationError)) throw e;
          const errors: Record<string, string> = {};
          for (const err of e.inner ?? []) {
            if (err.path && !errors[err.path]) errors[err.path] = err.message;
          }
          return errors;
        }
      }}
      validateOnBlur
      validateOnChange
      onSubmit={handleSearch}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched, isValid }) => (
        <Modal
          title={titleComponent}
          show={show}
          onClose={onClose}
          titleClassName='pl-20 text-black'
          childrenClassName='modal-panel-grey pl-20 pr-20 pb-2 pt-2'
        >
          <PurchaseNumbersForm
            values={values}
            errors={errors}
            touched={touched}
            isValid={isValid}
            isSearching={isSearching}
            allProviderNames={allProviderNames}
            allTcrCampaigns={allTcrCampaigns}
            tcrCampaignsLoading={tcrCampaignsLoading}
            records={records}
            searchResult={searchResult}
            searchErrors={searchErrors}
            onSubmit={handleSubmit}
            onReview={onReview}
            onConfirm={onConfirm}
            isConfirming={isConfirming}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            onClearSearch={() => { setSearchResult(null); setSearchErrors([]); }}
          />
        </Modal>
      )}
    </Formik>
  );
};

export default PurchaseNumbersModal;
